import React from 'react'
import {
  Line,
  Bar,
  Scatter,
  Doughnut,
} from 'react-chartjs-2'
import { theme } from '../theme'

// Chart default options with Dory brand styling
const getDefaultOptions = (type, title) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        color: theme.colors.secondary.black,
        font: {
          family: theme.typography.body.fontFamily,
          size: 12,
        },
        usePointStyle: true,
        padding: 20,
      },
    },
    title: {
      display: !!title,
      text: title,
      color: theme.colors.primary.main,
      font: {
        family: theme.typography.heading.fontFamily,
        size: 16,
        weight: 600,
      },
      padding: {
        bottom: 20,
      },
    },
    tooltip: {
      backgroundColor: theme.colors.primary.main,
      titleColor: 'white',
      bodyColor: 'white',
      borderColor: theme.colors.primary.light,
      borderWidth: 1,
      cornerRadius: 8,
      titleFont: {
        family: theme.typography.body.fontFamily,
        weight: 500,
      },
      bodyFont: {
        family: theme.typography.body.fontFamily,
      },
    },
  },
  scales: type !== 'doughnut' ? {
    x: {
      grid: {
        color: theme.colors.primary.light + '20',
        borderColor: theme.colors.primary.light,
      },
      ticks: {
        color: theme.colors.secondary.black,
        font: {
          family: theme.typography.body.fontFamily,
        },
      },
    },
    y: {
      grid: {
        color: theme.colors.primary.light + '20',
        borderColor: theme.colors.primary.light,
      },
      ticks: {
        color: theme.colors.secondary.black,
        font: {
          family: theme.typography.body.fontFamily,
        },
      },
    },
  } : undefined,
})

// Get Dory brand colors for charts
export const getChartColors = () => ({
  primary: theme.colors.primary.main,
  secondary: theme.colors.secondary.green,
  tertiary: theme.colors.primary.light,
  quaternary: theme.colors.primary.dark,
  warning: theme.colors.status.warning,
  error: theme.colors.status.error,
  success: theme.colors.status.success,
  gradient: (ctx, chartArea, colorStop1, colorStop2) => {
    if (!chartArea) return null
    
    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
    gradient.addColorStop(0, colorStop1 || theme.colors.primary.light + '40')
    gradient.addColorStop(1, colorStop2 || theme.colors.primary.main + '80')
    return gradient
  },
})

// Line Chart Component
export const LineChart = ({ data, options = {}, title, height = 400 }) => {
  const chartOptions = {
    ...getDefaultOptions('line', title),
    ...options,
  }
  
  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <Line data={data} options={chartOptions} />
    </div>
  )
}

// Bar Chart Component
export const BarChart = ({ data, options = {}, title, height = 400 }) => {
  const chartOptions = {
    ...getDefaultOptions('bar', title),
    ...options,
  }
  
  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <Bar data={data} options={chartOptions} />
    </div>
  )
}

// Scatter Plot Component
export const ScatterChart = ({ data, options = {}, title, height = 400 }) => {
  const chartOptions = {
    ...getDefaultOptions('scatter', title),
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        grid: {
          color: theme.colors.primary.light + '20',
          borderColor: theme.colors.primary.light,
        },
        ticks: {
          color: theme.colors.secondary.black,
          font: {
            family: theme.typography.body.fontFamily,
          },
        },
        title: {
          display: true,
          text: 'Pressure Level',
          color: theme.colors.primary.main,
          font: {
            family: theme.typography.body.fontFamily,
            weight: 500,
          },
        },
      },
      y: {
        grid: {
          color: theme.colors.primary.light + '20',
          borderColor: theme.colors.primary.light,
        },
        ticks: {
          color: theme.colors.secondary.black,
          font: {
            family: theme.typography.body.fontFamily,
          },
        },
        title: {
          display: true,
          text: 'Emotional Score',
          color: theme.colors.primary.main,
          font: {
            family: theme.typography.body.fontFamily,
            weight: 500,
          },
        },
      },
    },
    ...options,
  }
  
  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <Scatter data={data} options={chartOptions} />
    </div>
  )
}

// Doughnut Chart Component
export const DoughnutChart = ({ data, options = {}, title, height = 400 }) => {
  const chartOptions = {
    ...getDefaultOptions('doughnut', title),
    ...options,
  }
  
  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <Doughnut data={data} options={chartOptions} />
    </div>
  )
}

// Helper function to create resilience scatter plot data
export const createResilienceScatterData = (entries) => {
  const colors = getChartColors()
  
  return {
    datasets: [
      {
        label: 'Emotional Response to Pressure',
        data: entries.map(entry => ({
          x: entry.pressure,
          y: entry.emotionalScore,
        })),
        backgroundColor: colors.primary + '80',
        borderColor: colors.primary,
        borderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  }
}

// Helper function to create metric comparison data
export const createMetricComparisonData = (users) => {
  const colors = getChartColors()
  
  return {
    labels: ['Mental Resilience'],
    datasets: [
      {
        label: 'Average Scores',
        data: [
          users.reduce((sum, user) => sum + (user.metrics?.resilience?.score || 0), 0) / users.length,
        ],
        backgroundColor: [colors.primary],
        borderColor: [colors.primary],
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  }
} 